// SNIGJ - Offline Storage Service using IndexedDB
// Provides offline capability for Border Police app

const DB_NAME = 'snigj_border_police';
const DB_VERSION = 1;

class OfflineStorageService {
  constructor() {
    this.db = null;
    this.isOnline = navigator.onLine;
    this.syncQueue = [];
    
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Fugitives store - most wanted list cached for offline
        if (!db.objectStoreNames.contains('fugitives')) {
          const fugitivesStore = db.createObjectStore('fugitives', { keyPath: 'fugitive_id' });
          fugitivesStore.createIndex('national_id', 'national_id', { unique: false });
          fugitivesStore.createIndex('passport_number', 'passport_number', { unique: false });
          fugitivesStore.createIndex('full_name', 'full_name', { unique: false });
          fugitivesStore.createIndex('danger_level', 'danger_level', { unique: false });
          fugitivesStore.createIndex('synced_at', 'synced_at', { unique: false });
        }

        // Offline scans - scans performed while offline
        if (!db.objectStoreNames.contains('offline_scans')) {
          const scansStore = db.createObjectStore('offline_scans', { keyPath: 'local_id', autoIncrement: true });
          scansStore.createIndex('created_at', 'created_at', { unique: false });
          scansStore.createIndex('synced', 'synced', { unique: false });
        }

        // Sync metadata
        if (!db.objectStoreNames.contains('sync_meta')) {
          db.createObjectStore('sync_meta', { keyPath: 'key' });
        }
      };
    });
  }

  handleOnline() {
    this.isOnline = true;
    console.log('Back online - syncing data...');
    this.syncOfflineScans();
  }

  handleOffline() {
    this.isOnline = false;
    console.log('Gone offline - using cached data');
  }

  // ==================== FUGITIVES ====================

  async cacheFugitives(fugitives) {
    if (!this.db) await this.init();

    const tx = this.db.transaction('fugitives', 'readwrite');
    const store = tx.objectStore('fugitives');

    // Clear old data
    await new Promise((resolve, reject) => {
      const clearRequest = store.clear();
      clearRequest.onsuccess = resolve;
      clearRequest.onerror = reject;
    });

    // Add new data with sync timestamp
    const syncedAt = new Date().toISOString();
    for (const fugitive of fugitives) {
      store.put({ ...fugitive, synced_at: syncedAt });
    }

    // Update sync metadata
    await this.updateSyncMeta('fugitives_last_sync', syncedAt);
    await this.updateSyncMeta('fugitives_count', fugitives.length);

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve(fugitives.length);
      tx.onerror = () => reject(tx.error);
    });
  }

  async getCachedFugitives() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('fugitives', 'readonly');
      const store = tx.objectStore('fugitives');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async searchFugitivesOffline(query) {
    const fugitives = await this.getCachedFugitives();
    
    if (!query) return fugitives;

    const lowerQuery = query.toLowerCase();
    return fugitives.filter(f => 
      f.national_id?.includes(query) ||
      f.passport_number?.toLowerCase().includes(lowerQuery) ||
      f.full_name?.toLowerCase().includes(lowerQuery)
    );
  }

  async checkFugitiveOffline(identifiers) {
    const fugitives = await this.getCachedFugitives();
    
    for (const fugitive of fugitives) {
      if (identifiers.national_id && fugitive.national_id === identifiers.national_id) {
        return { match: true, fugitive, match_type: 'national_id' };
      }
      if (identifiers.passport_number && fugitive.passport_number?.toLowerCase() === identifiers.passport_number.toLowerCase()) {
        return { match: true, fugitive, match_type: 'passport_number' };
      }
      if (identifiers.full_name) {
        const nameParts = identifiers.full_name.toLowerCase().split(' ');
        const fugitiveNameParts = fugitive.full_name?.toLowerCase().split(' ') || [];
        const matchCount = nameParts.filter(part => fugitiveNameParts.some(fp => fp.includes(part))).length;
        if (matchCount >= 2 || (nameParts.length === 1 && matchCount === 1)) {
          return { match: true, fugitive, match_type: 'name_partial' };
        }
      }
    }

    return { match: false, fugitive: null };
  }

  // ==================== OFFLINE SCANS ====================

  async saveOfflineScan(scanData) {
    if (!this.db) await this.init();

    const offlineScan = {
      ...scanData,
      created_at: new Date().toISOString(),
      synced: false,
      offline: true
    };

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('offline_scans', 'readwrite');
      const store = tx.objectStore('offline_scans');
      const request = store.add(offlineScan);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getOfflineScans() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('offline_scans', 'readonly');
      const store = tx.objectStore('offline_scans');
      const request = store.getAll();

      request.onsuccess = () => {
        // Filter for unsynced scans (synced === false)
        const unsynced = (request.result || []).filter(scan => scan.synced === false);
        resolve(unsynced);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async markScanSynced(localId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('offline_scans', 'readwrite');
      const store = tx.objectStore('offline_scans');
      const getRequest = store.get(localId);

      getRequest.onsuccess = () => {
        const scan = getRequest.result;
        if (scan) {
          scan.synced = true;
          scan.synced_at = new Date().toISOString();
          const putRequest = store.put(scan);
          putRequest.onsuccess = () => resolve(true);
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve(false);
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async syncOfflineScans() {
    const offlineScans = await this.getOfflineScans();
    
    if (offlineScans.length === 0) return { synced: 0 };

    let syncedCount = 0;
    const token = localStorage.getItem('snigj_token');

    for (const scan of offlineScans) {
      try {
        const response = await fetch('/api/border/scan/offline-sync', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...scan,
            offline_created_at: scan.created_at
          })
        });

        if (response.ok) {
          await this.markScanSynced(scan.local_id);
          syncedCount++;
        }
      } catch (error) {
        console.error('Failed to sync scan:', error);
      }
    }

    return { synced: syncedCount, total: offlineScans.length };
  }

  // ==================== SYNC METADATA ====================

  async updateSyncMeta(key, value) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('sync_meta', 'readwrite');
      const store = tx.objectStore('sync_meta');
      const request = store.put({ key, value, updated_at: new Date().toISOString() });

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async getSyncMeta(key) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('sync_meta', 'readonly');
      const store = tx.objectStore('sync_meta');
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result?.value);
      request.onerror = () => reject(request.error);
    });
  }

  async getLastSyncInfo() {
    const lastSync = await this.getSyncMeta('fugitives_last_sync');
    const count = await this.getSyncMeta('fugitives_count');
    const pendingScans = (await this.getOfflineScans()).length;

    return {
      lastSync,
      cachedFugitives: count || 0,
      pendingScans,
      isOnline: this.isOnline
    };
  }

  // ==================== UTILITIES ====================

  async clearAllData() {
    if (!this.db) await this.init();

    const stores = ['fugitives', 'offline_scans', 'sync_meta'];
    
    for (const storeName of stores) {
      await new Promise((resolve, reject) => {
        const tx = this.db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const request = store.clear();
        request.onsuccess = resolve;
        request.onerror = reject;
      });
    }

    return true;
  }
}

// Singleton instance
const offlineStorage = new OfflineStorageService();

export default offlineStorage;
